import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  estimateApi,
  type EstimateExportFormat,
} from '../api/estimate-service';
import { toast } from '@/shared/lib/toast';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('useEstimateExport');

/**
 * Download an estimate as a file (PDF / Excel / Text). Fetches the blob from
 * the export endpoint (authenticated, raw body — see `estimateApi.export`) and
 * triggers a browser download via a hidden `<a download>`, using the
 * server-supplied filename. `exportingFormat` lets the caller show a per-item
 * spinner without blocking the other format options.
 */
export function useEstimateExport(estimateId: string) {
  const t = useTranslations('common.estimate.export');
  const [exportingFormat, setExportingFormat] =
    useState<EstimateExportFormat | null>(null);

  const exportEstimate = useCallback(
    async (format: EstimateExportFormat) => {
      if (exportingFormat) return;
      setExportingFormat(format);
      try {
        const { blob, filename } = await estimateApi.export(estimateId, format);
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        // Revoke after a tick so the click-initiated download keeps the URL.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      } catch (error) {
        logger.error('Estimate export failed', { estimateId, format, error });
        toast.error(t('failed'));
      } finally {
        setExportingFormat(null);
      }
    },
    [estimateId, exportingFormat, t]
  );

  return { exportEstimate, exportingFormat, isExporting: exportingFormat !== null };
}
